import { createAppSelector, selectClassUsers, selectCurrentCourseId } from "./base";
import { selectIsClassSessionInPerson, selectIsClassSessionVirtual } from "./class-session";
import { selectCourseUserIdToUserIdMap } from "./course";
import { selectCourseUserIdByUserId, selectCurrentCourseUserId } from "./course-user";

import { ClassUserType } from "components/server-types";

export const selectCurrentClassUserList = createAppSelector(
  [selectCurrentCourseId, selectClassUsers],
  (currentCourseId, classUsers): ClassUserType[] => {
    if (!currentCourseId) {
      return [];
    }
    // Find the class users that match the current course
    return classUsers.filter((classUser) => classUser.course_id === currentCourseId);
  },
);

export const selectClassUserById = createAppSelector(
  [(state, classUserId: string) => classUserId, selectCurrentClassUserList],
  (classUserId, classUsers): ClassUserType | undefined =>
    classUsers.find((classUser) => classUser.id === classUserId),
);

export const selectClassUserByCourseUserId = createAppSelector(
  [(state, courseUserId: string) => courseUserId, selectCurrentClassUserList],
  (courseUserId, classUsers): ClassUserType | undefined =>
    classUsers.find((cu) => cu.course_user_id === courseUserId),
);

// Map of class user ids to user ids
// TODO: this could be optimized since the ids should not change that often
export const selectClassUserIdToUserIdMap = createAppSelector(
  [selectClassUsers, selectCourseUserIdToUserIdMap],
  (classUsers, courseUserIdToUserIdMap) => {
    const classUserIdToUserIdMap = new Map<string, string | null>();
    classUsers.forEach((classUser) => {
      classUserIdToUserIdMap.set(
        classUser.id,
        courseUserIdToUserIdMap.get(classUser.course_user_id) || null,
      );
    });
    return classUserIdToUserIdMap;
  },
);

export const selectUserByClassUserId = createAppSelector(
  [
    selectClassUserIdToUserIdMap,
    (state) => state.users,
    (state, classUserId: string) => classUserId,
  ],
  (classUserIdToUserIdMap, users, classUserId) => {
    const userId = classUserIdToUserIdMap.get(classUserId) || null;
    return userId && userId in users ? users[userId] : null;
  },
);

export const selectUserIdByClassUserId = createAppSelector(
  [selectClassUserIdToUserIdMap, (state, classUserId: string) => classUserId],
  (classUserIdToUserIdMap, classUserId) => classUserIdToUserIdMap.get(classUserId) || null,
);

export const selectClassUserByUserId = createAppSelector(
  [selectClassUsers, selectCourseUserIdByUserId],
  (classUsers, courseUserId) => classUsers.find((cu) => cu.course_user_id === courseUserId),
);

export const selectClassUserIdByUserId = createAppSelector(
  [selectClassUserByUserId],
  (classUser) => classUser?.id || null,
);

export const selectClassUserIsInVirtualClassByUserId = createAppSelector(
  [selectClassUserByUserId, selectIsClassSessionVirtual],
  (classUser, isClassSessionVirtual) =>
    isClassSessionVirtual && (classUser?.is_in_class_session || false),
);

export const selectCurrentClassUser = createAppSelector(
  [selectCurrentClassUserList, selectCurrentCourseUserId],
  (classUsers, currentCourseUserId): ClassUserType | undefined => {
    if (!classUsers || !currentCourseUserId) {
      return undefined;
    }
    // Find the class user that matches the current course user
    return classUsers.find((classUser) => classUser.course_user_id === currentCourseUserId);
  },
);

export const selectCurrentClassUserId = createAppSelector(
  [selectCurrentClassUser],
  (classUser) => classUser?.id || null,
);

export const selectCurrentClassUserReaction = createAppSelector(
  [selectCurrentClassUser],
  (classUser) => classUser?.reaction || null,
);

export const selectCurrentClassUserVote = createAppSelector(
  [selectCurrentClassUser],
  (classUser) => classUser?.vote || null,
);

export const selectIsHandRaisedByClassUserId = createAppSelector(
  [(state, classUserId: string) => classUserId, selectCurrentClassUserList],
  (classUserId, classUsers): boolean => {
    const classUser = classUsers.find((cu) => cu.id === classUserId);
    return !!classUser?.hand_raised_at;
  },
);

export const selectCurrentClassUserIsInPersonClass = createAppSelector(
  [selectCurrentClassUser, selectIsClassSessionInPerson],
  (classUser, isClassSessionInPerson) =>
    isClassSessionInPerson && (classUser?.is_in_class_session || false),
);

// NOTE: this is not the same is isInVirtualClass from useAv(), which takes into account
// the main AV tab and classUser.is_performing_av_check. Use this selector only if you
// don't need to check those conditions.
export const selectCurrentClassUserIsInVirtualClass = createAppSelector(
  [selectCurrentClassUser, selectIsClassSessionVirtual],
  (classUser, isClassSessionVirtual) =>
    isClassSessionVirtual && (classUser?.is_in_class_session || false),
);

export const selectCurrentClassUserIsInClassSession = createAppSelector(
  [selectCurrentClassUserIsInPersonClass, selectCurrentClassUserIsInVirtualClass],
  (isInPersonClass, isInVirtualClass) => isInPersonClass || isInVirtualClass,
);

export const selectCurrentClassUserBreakoutGroupId = createAppSelector(
  [selectCurrentClassUser],
  (classUser) => classUser?.breakout_group_id || null,
);

export const selectCurrentClassUserIsPerformingAvCheck = createAppSelector(
  [selectCurrentClassUser],
  (classUser) => classUser?.is_performing_av_check || false,
);
